/** Auto-provisioned ledger account bindings on workspace entities. */

export interface iBrandDefaultLedgerAccounts {
  accountsReceivableLedgerId: string;
  revenueLedgerId: string;
  vatLiabilityLedgerId?: string;
}

export interface iCustomerPaymentMethodDefaultLedgerAccounts {
  receivingLedgerId: string;
  pdqFeesLedgerId: string;
  clearingLedgerId: string;
}

export interface iSupplierDefaultLedgerAccounts {
  expenseLedgerId: string;
  accountsPayableLedgerId: string;
}

export interface iSupplierPaymentMethodDefaultLedgerAccounts {
  clearingLedgerId: string;
}

export interface iOrganizationDefaultLedgerAccounts {
  payrollExpenseLedgerId: string;
  softwareSubscriptionsExpenseLedgerId: string;
  marketingAdvertisingExpenseLedgerId: string;
  rentPremisesExpenseLedgerId: string;
  professionalComplianceFeesExpenseLedgerId: string;
  bankFinanceChargesExpenseLedgerId: string;
  telecomUtilitiesExpenseLedgerId: string;
  mainBankAccountLedgerId: string;
  cashLedgerId: string;
  tradeExpenseClearingLedgerId: string;
  customerCreditVoucherLiabilityLedgerId: string;
}

export type WorkspaceDefaultLedgerKey = keyof iOrganizationDefaultLedgerAccounts;

export type WorkspaceDefaultLedgerDefinition = {
  key: WorkspaceDefaultLedgerKey;
  label: string;
  group: "expense" | "cash" | "liability";
};

export const WORKSPACE_DEFAULT_LEDGER_DEFINITIONS: WorkspaceDefaultLedgerDefinition[] =
  [
    { key: "payrollExpenseLedgerId", label: "Payroll", group: "expense" },
    {
      key: "softwareSubscriptionsExpenseLedgerId",
      label: "Software & Subscriptions",
      group: "expense",
    },
    {
      key: "marketingAdvertisingExpenseLedgerId",
      label: "Marketing & Advertising",
      group: "expense",
    },
    {
      key: "rentPremisesExpenseLedgerId",
      label: "Rent & Premises",
      group: "expense",
    },
    {
      key: "professionalComplianceFeesExpenseLedgerId",
      label: "Professional & Compliance Fees",
      group: "expense",
    },
    {
      key: "bankFinanceChargesExpenseLedgerId",
      label: "Bank & Finance Charges",
      group: "expense",
    },
    {
      key: "telecomUtilitiesExpenseLedgerId",
      label: "Telecom & Utilities",
      group: "expense",
    },
    {
      key: "mainBankAccountLedgerId",
      label: "Main Bank Account",
      group: "cash",
    },
    { key: "cashLedgerId", label: "Cash", group: "cash" },
    {
      key: "tradeExpenseClearingLedgerId",
      label: "Trade Expense Clearing",
      group: "cash",
    },
    {
      key: "customerCreditVoucherLiabilityLedgerId",
      label: "Customer Credit Vouchers",
      group: "liability",
    },
  ];

/** Workspace default ledgers selectable as payment-method connected accounts. */
export const WORKSPACE_PAYMENT_ACCOUNT_LEDGER_KEYS = [
  "mainBankAccountLedgerId",
  "cashLedgerId",
] as const satisfies readonly WorkspaceDefaultLedgerKey[];

export function getWorkspacePaymentAccountLedgerIds(
  accounts?: iOrganizationDefaultLedgerAccounts | null
): Set<string> {
  const ids = new Set<string>();
  for (const key of WORKSPACE_PAYMENT_ACCOUNT_LEDGER_KEYS) {
    const id = accounts?.[key]?.trim();
    if (id) {
      ids.add(id);
    }
  }
  return ids;
}

export function isOrganizationDefaultLedgersComplete(
  accounts?: iOrganizationDefaultLedgerAccounts | null
): boolean {
  return WORKSPACE_DEFAULT_LEDGER_DEFINITIONS.every((definition) =>
    Boolean(accounts?.[definition.key]?.trim())
  );
}
